import { useCallback, useState } from 'react';

import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { randomUUID as uuid } from 'expo-crypto';
import { logRBIEvent } from 'features/analytics';
import {
  AuthenticationMethod,
  AuthenticationPhases,
  EventTypes,
} from 'features/analytics/constants';
import { logSignInEvent, logSignUpEvent, signOutEvent } from 'features/auth/utils/analytics';
import { AuthenticationPath } from 'features/auth/utils/analytics';

import {
  ISocialLoginInput,
  IsoCountryCode,
  ProviderType,
  useCreateOtpIfUserExistsMutation,
  useSignUpMutation,
  useSocialLoginMutation,
  useValidateAuthJwtMutation,
  useValidateOtpMutation,
} from 'generated/graphql-gateway';
import { ModalCb } from 'hooks/use-error-modal';
import {
  signOut as cognitoSignOut,
  signUp as cognitoSignUp,
  validateLogin as cognitoValidateLogin,
  commitAuthenticatedSession,
} from 'remote/auth/cognito';
import { AuthTransitionReason } from 'state/auth/session-transition';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { platform, welcomeEmailDomain } from 'utils/environment';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import { toError } from 'utils/errors';
import logger from 'utils/logger';

import { SIGN_IN_FAIL } from '../constants';
import { JwtValidationError, OtpValidationError, UserNotFoundError } from '../errors';
import { IOTPSignInParams, ISignUpResult, ModalAuthScreen, ModalAuthState } from '../types';

export interface IUseAccountAuthentication {
  refreshCurrentUser(): Promise<void>;
  openErrorDialog: ModalCb;
  transitionCurrentUser(
    reason: AuthTransitionReason,
    commitCredentials: () => Promise<CognitoUserSession | null>
  ): Promise<CognitoUserSession | null>;
  setModalAuthState(modalAuthState: Partial<ModalAuthState>): void;
  modalAuthIsOpen: boolean;
}

interface IFavoriteStore {
  storeId?: string;
  storeNumber: string;
}

interface ISignUp {
  email: string;
  name: string;
  dob?: string;
  phoneNumber: string;
  country: string;
  wantsPromotionalEmails: boolean;
  zipcode?: string;
  favoriteStores?: IFavoriteStore[];
  crmAttributes?: { source: string };
}

interface IValidateLogin {
  jwt: string;
  username: string;
}

interface IGetSesionIdAndChallengeCodeOtp {
  email?: string;
  phoneNumber?: string;
  otpCode: string;
  sessionId: string;
}

type IStoreOtpCredentials = { sessionId: string } & IOTPSignInParams;

export const getStoredOtpCredentials = () =>
  syncStorage.getObject<IStoreOtpCredentials>(SyncStorageKey.OTP);

export const storeOtpCredentials = (data: IStoreOtpCredentials) =>
  syncStorage.setObject(SyncStorageKey.OTP, data);

export const useAccountAuthentication = ({
  refreshCurrentUser,
  openErrorDialog,
  transitionCurrentUser,
  setModalAuthState,
  modalAuthIsOpen,
}: IUseAccountAuthentication) => {
  const enableSignUpInBE = useFlag(LaunchDarklyFlag.ENABLE_COGNITO_SIGNUP_IN_BE);
  const preloaded =
    syncStorage.getObject<{ callbackUrl?: string }>(SyncStorageKey.AUTH_REDIRECT) || {};

  const [originLocation, setOriginLoc] = useState<null | string>(preloaded.callbackUrl || null);
  const [validateLoginIsLoading, setValidateLoginIsLoading] = useState<boolean>(false);
  const authenticationPath = modalAuthIsOpen
    ? AuthenticationPath.CONTEXTUAL
    : AuthenticationPath.CONVENTIONAL;

  const [socialLoginMutation, { loading: socialLoginMutationLoading }] = useSocialLoginMutation();
  const [
    validateAuthJwtMutation,
    { loading: validateAuthMutationLoading },
  ] = useValidateAuthJwtMutation();
  const [
    createOtpIfUserExistsMutation,
    { loading: createOtpIfUserExistsMutationLoading },
  ] = useCreateOtpIfUserExistsMutation();
  const [validateOtpMutation, { loading: validateOtpMutationLoading }] = useValidateOtpMutation();
  const [signUpMutation] = useSignUpMutation();

  const getSessionIdAndChallengeCodeOtp = useCallback(
    async ({ email, otpCode, sessionId }: IGetSesionIdAndChallengeCodeOtp) => {
      const { data } = await validateOtpMutation({
        variables: {
          input: {
            code: otpCode,
            email,
            sessionId,
          },
        },
      });

      const { sessionId: validatedSessionId, challengeCode, email: validatedEmail } =
        data?.exchangeOTPCodeForCognitoCredentials ?? {};

      if (!validatedSessionId || !challengeCode) {
        throw new OtpValidationError('GraphQL validation failed');
      }

      return { sessionId: validatedSessionId, code: challengeCode, email: validatedEmail };
    },
    [validateOtpMutation]
  );

  const getSessionIdAndChallengeCode = useCallback(
    async (jwt: string) => {
      try {
        const { data } = await validateAuthJwtMutation({
          variables: {
            input: { jwt },
          },
        });
        const { sessionId, challengeCode } = data?.validateAuthJwt ?? {};
        if (!sessionId || !challengeCode) {
          throw new JwtValidationError();
        }

        return { sessionId, code: challengeCode };
      } catch (error) {
        if (
          (error as {
            originalError?: { message?: string }[];
          })?.originalError?.[0]?.message?.toLowerCase() === 'email not registered'
        ) {
          throw new UserNotFoundError();
        }
        throw error;
      }
    },
    [validateAuthJwtMutation]
  );

  const signOut = useCallback(
    async (isGlobalSignOut?: boolean) => {
      try {
        await transitionCurrentUser(AuthTransitionReason.Logout, async () => {
          await cognitoSignOut(isGlobalSignOut);
          return null;
        });
        signOutEvent(true);
      } catch (error) {
        logger.error({
          message: 'An error occurred signing out - useAccountAuthentication',
          error,
        });
        signOutEvent(false, toError(error).message);
        refreshCurrentUser();

        throw error;
      }
    },
    [transitionCurrentUser, refreshCurrentUser]
  );

  const signIn = useCallback(
    async ({ email }: IOTPSignInParams): Promise<void> => {
      try {
        const sessionId = uuid();

        const { data } = await createOtpIfUserExistsMutation({
          variables: {
            input: {
              email,
              platform: platform(),
              sessionId,
            },
          },
        });

        if (!data?.createOTPIfUserExists.success) {
          // Sign up
          setModalAuthState({
            screen: ModalAuthScreen.SIGN_UP,
            shouldDisplaySignInSuccessMessage: false,
            user: { email },
          });

          return;
        }

        // Sign in. We need to store the sessionId so that we can validate the OTP
        storeOtpCredentials({ email, sessionId });

        setModalAuthState({
          screen: ModalAuthScreen.OTP,
        });

        logSignInEvent({
          phase: AuthenticationPhases.START,
          authenticationMethod: AuthenticationMethod.OTP,
          authenticationPath,
        });

        // Log request
        logRBIEvent({
          name: 'Sign in with OTP Request',
          type: EventTypes.Other,
          attributes: {
            signUpType: 'Email',
          },
        });
      } catch (error) {
        logSignInEvent({
          phase: AuthenticationPhases.ERROR,
          message: toError(error).message ?? '',
          authenticationMethod: AuthenticationMethod.OTP,
          authenticationPath,
        });
        ((error as unknown) as { code: string }).code = SIGN_IN_FAIL;
        throw error;
      }
    },
    [authenticationPath, setModalAuthState, createOtpIfUserExistsMutation]
  );

  const signUp = useCallback(
    async (
      {
        email,
        name,
        dob,
        phoneNumber,
        country,
        wantsPromotionalEmails,
        zipcode,
        favoriteStores,
        crmAttributes,
      }: ISignUp,
      signInOverride: (args: IOTPSignInParams) => Promise<void> = signIn,
      authenticationMethod?: AuthenticationMethod,
      socialAuthenticationType?: ProviderType
    ): Promise<ISignUpResult> => {
      let jwt;

      const logObject = {
        authenticationMethod,
        socialAuthenticationType,
        authenticationPath,
      };

      try {
        logSignUpEvent({
          ...logObject,
          phase: AuthenticationPhases.START,
        });

        if (enableSignUpInBE) {
          logSignUpEvent({
            ...logObject,
            phase: AuthenticationPhases.VALIDATION,
          });

          const { data } = await signUpMutation({
            variables: {
              input: {
                country: country as IsoCountryCode,
                dob,
                name,
                phoneNumber,
                platform: platform(),
                providerType: socialAuthenticationType,
                stage: welcomeEmailDomain(),
                userName: email,
                wantsPromotionalEmails,
                zipcode,
                favoriteStores,
                ...(crmAttributes && { crmAttributes }),
              },
            },
          });
          jwt = data?.signUp;
        } else {
          await cognitoSignUp({
            name,
            phoneNumber,
            username: email,
            country,
            wantsPromotionalEmails,
            dob,
          });
        }
      } catch (error) {
        logSignUpEvent({
          message: toError(error).message,
          authenticationMethod,
          socialAuthenticationType,
          authenticationPath,
          phase: AuthenticationPhases.ERROR,
        });

        throw error;
      }

      await signInOverride({
        email,
      });

      return { jwt };
    },
    [authenticationPath, enableSignUpInBE, signIn, signUpMutation]
  );

  const validateLoginOtp = useCallback(
    async ({ otpCode }: { otpCode: string }) => {
      const commonLogObject = {
        authenticationMethod: AuthenticationMethod.OTP,
        authenticationPath,
      };

      try {
        logSignInEvent({
          ...commonLogObject,
          phase: AuthenticationPhases.VALIDATION,
        });

        let { email } = getStoredOtpCredentials() ?? {};
        const { sessionId: storedSessionId } = getStoredOtpCredentials() ?? {};

        if (!storedSessionId) {
          throw new OtpValidationError('Missing sessionId');
        }

        const validateOtpResponse = await getSessionIdAndChallengeCodeOtp({
          email,
          otpCode,
          sessionId: storedSessionId,
        });

        if (!email && validateOtpResponse.email) {
          email = validateOtpResponse.email;
        }

        // Edge case: Email might be missing if the user crosses devices (Device B requesting code for Device A),
        // and the backend payload additionally fails to return the email in `validateOtpResponse`.
        // TODO: In the future, we could throw a specific error code here to prompt the UI to restart the flow
        // with a clearer "Session Expired" message instead of the generic "invalidOtpProvided" error.
        if (!email) {
          throw new OtpValidationError('Missing email');
        }

        const { sessionId, code } = validateOtpResponse;

        await transitionCurrentUser(AuthTransitionReason.OtpLogin, async () => {
          const session = await cognitoValidateLogin({ username: email, code, sessionId });
          if (!session) {
            throw new Error('Cognito OTP validation returned no session');
          }
          return commitAuthenticatedSession(session);
        });

        logSignInEvent({
          ...commonLogObject,
          phase: AuthenticationPhases.COMPLETE,
        });
      } catch (error) {
        logSignInEvent({
          ...commonLogObject,
          phase: AuthenticationPhases.ERROR,
          message: toError(error).message,
        });

        throw error;
      }
    },
    [getSessionIdAndChallengeCodeOtp, authenticationPath, transitionCurrentUser]
  );

  const validateLogin = useCallback(
    async ({ jwt, username }: IValidateLogin) => {
      setValidateLoginIsLoading(true);
      try {
        const { sessionId, code } = await getSessionIdAndChallengeCode(jwt);
        await transitionCurrentUser(AuthTransitionReason.JwtLogin, async () => {
          const session = await cognitoValidateLogin({ username, code, sessionId });
          if (!session) {
            throw new Error('Cognito JWT validation returned no session');
          }
          return commitAuthenticatedSession(session);
        });
        logSignInEvent({ phase: AuthenticationPhases.COMPLETE });
        // Add a marker when a user is successfull login to track unexpected sign outs
        syncStorage.setBoolean(SyncStorageKey.USER_SIGNED_IN_SUCCESSFULLY, true);
      } catch (error) {
        logSignInEvent({
          phase: AuthenticationPhases.ERROR,
          message: toError(error).message,
        });

        openErrorDialog({
          error: toError(error),
          message: t('authError'),
          modalAppearanceEventMessage: 'Error: JWT Validation Failure',
        });

        throw error;
      } finally {
        setValidateLoginIsLoading(false);
      }
    },
    [getSessionIdAndChallengeCode, openErrorDialog, transitionCurrentUser]
  );

  const socialLogin = useCallback(
    async (input: ISocialLoginInput) => {
      const commonLogObject = {
        authenticationMethod: AuthenticationMethod.SOCIAL,
        socialAuthenticationType: input.providerType,
        authenticationPath,
      };

      try {
        const response = await socialLoginMutation({
          variables: {
            input,
          },
        });

        logSignInEvent({
          phase: AuthenticationPhases.VALIDATION,
          ...commonLogObject,
        });

        await transitionCurrentUser(AuthTransitionReason.SocialLogin, async () => {
          const session = await cognitoValidateLogin({
            username: response.data?.socialLogin?.email || '',
            code: response.data?.socialLogin?.challengeCode || '',
            sessionId: response.data?.socialLogin?.sessionId || '',
          });
          if (!session) {
            throw new Error('Cognito social validation returned no session');
          }
          return commitAuthenticatedSession(session);
        });

        logSignInEvent({
          phase: AuthenticationPhases.COMPLETE,
          ...commonLogObject,
        });

        // Add a marker when a user is successfull login to track unexpected sign outs
        syncStorage.setBoolean(SyncStorageKey.USER_SIGNED_IN_SUCCESSFULLY, true);
      } catch (error) {
        logSignInEvent({
          phase: AuthenticationPhases.ERROR,
          message: toError(error).message,
          ...commonLogObject,
        });

        throw error;
      }
    },
    [authenticationPath, transitionCurrentUser, socialLoginMutation]
  );

  return {
    authLoading:
      validateAuthMutationLoading ||
      createOtpIfUserExistsMutationLoading ||
      validateOtpMutationLoading ||
      socialLoginMutationLoading ||
      validateLoginIsLoading,
    originLocation,
    setOriginLoc,
    signIn,
    signOut,
    signUp,
    socialLogin,
    validateLogin,
    validateLoginOtp,
  };
};
